import React, { memo, useState } from "react";
import { HelpCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { LoraDTO } from "@/types";
interface LoraCellProps {
  lora: LoraDTO;
  initialWeight: number;
  onFinalWeightChange: (id: string, finalWeight: number) => void;
}

/**
 * LoraCell:
 * - Renders a slider to control the weight of a given LORA.
 * - Maintains local state (localWeight) for intermediate slider changes
 *   to ensure smooth dragging without frequent global state updates.
 * - Calls onFinalWeightChange once the user finishes dragging (mouse up).
 */
const LoraCell: React.FC<LoraCellProps> = ({
  lora,
  initialWeight,
  onFinalWeightChange
}) => {
  const [localWeight, setLocalWeight] = useState(initialWeight);
  return <div className="flex flex-col mt-2" onMouseDown={e => {
    // Prevent parent click events when interacting with the slider
    e.stopPropagation();
  }} onClick={e => {
    // Prevent parent click events when interacting with the slider
    e.stopPropagation();
  }} data-sentry-component="LoraCell" data-sentry-source-file="index.tsx">
            <div className="flex space-x-2">
                <input id={lora.id} type="range" min={-2} max={2} step={0.01} value={localWeight} onChange={e => {
        setLocalWeight(parseFloat(e.target.value));
      }} onMouseUp={() => {
        // Commit the final weight to the parent once the user finishes dragging
        onFinalWeightChange(lora.id, localWeight);
      }} onTouchEnd={() => {
        // Also handle touch interactions
        onFinalWeightChange(lora.id, localWeight);
      }} />
                <Input type="number" value={localWeight.toFixed(2)} onChange={e => {
        const value = parseFloat(e.target.value);
        if (!isNaN(value) && value >= -2 && value <= 2) {
          onFinalWeightChange(lora.id, value);
        }
      }} className="w-16 text-center" min={-2} max={2} step={0.1} data-sentry-element="Input" data-sentry-source-file="index.tsx" />
            </div>
            <Popover data-sentry-element="Popover" data-sentry-source-file="index.tsx">
                <PopoverTrigger asChild data-sentry-element="PopoverTrigger" data-sentry-source-file="index.tsx">
                    <Button variant="ghost" className="text-gray-300 hover:text-white !px-0 !py-0 hover:bg-gray-800 !rounded-full transition-all duration-200 md:!flex-initial" aria-label="More options" data-sentry-element="Button" data-sentry-source-file="index.tsx">
                        <div className="text-center text-xs font-semibold mt-1 flex flex-row">
                            Weight: {localWeight.toFixed(2)}
                            <HelpCircle className="h-4 w-4 text-[#7AA8E3] cursor-help" data-sentry-element="HelpCircle" data-sentry-source-file="index.tsx" />
                        </div>
                    </Button>
                </PopoverTrigger>
                <PopoverContent className="!rounded-xl border !border-slate-200/20 !bg-slate-950 p-4" data-sentry-element="PopoverContent" data-sentry-source-file="index.tsx">
                    <div className="text-xs text-gray-300 text-left">
                        <p className="mb-2">
                            <strong>What does &quot;Weight&quot; mean?</strong>
                        </p>
                        <p className="mb-2">
                            The weight controls how much this LORA affects the
                            final image. Here&apos;s a quick guide:
                        </p>
                        <ul className="list-disc pl-5 space-y-1">
                            <li>
                                <strong>1 (Default):</strong> Just right! The
                                LORA adds its effect at a balanced level.
                            </li>
                            <li>
                                <strong>0:</strong> Turned off. The LORA doesn’t
                                affect the image.
                            </li>
                            <li>
                                <strong>+2:</strong> Very strong. The image will
                                heavily follow the LORA&apos;s style or subject.
                            </li>
                            <li>
                                <strong>-1:</strong> Reverse! It tries to create
                                the opposite of what the LORA usually does.
                            </li>
                        </ul>
                        <p className="mt-2">
                            <strong>Small changes matter:</strong> Adjusting the
                            weight by just 0.1 or 0.2 can create subtle
                            differences. Experiment to see how the LORA blends
                            with your image!
                        </p>
                    </div>
                </PopoverContent>
            </Popover>
        </div>;
};

/**
 * Custom comparison function for React.memo:
 * We only re-render if the lora's ID, initialWeight, or isSelected status changes.
 * No need to re-render if these values remain the same.
 */
function areEqual(prevProps: LoraCellProps, nextProps: LoraCellProps) {
  return prevProps.lora.id === nextProps.lora.id && prevProps.initialWeight === nextProps.initialWeight;
}
export default memo(LoraCell, areEqual);